import type { TraceStep } from '../types'

export type MergePlan = {
  targetContactId: string
  mergeContactId: string | null
  reason: string | null
}

const mergeContactIdFromPlan = (plan: Record<string, unknown>): string | null => {
  if (typeof plan.mergeContactId === 'string') {
    return plan.mergeContactId
  }
  if (Array.isArray(plan.mergeContactIds) && typeof plan.mergeContactIds[0] === 'string') {
    return plan.mergeContactIds[0]
  }
  return null
}

export const parseMergeMessage = (message: string): MergePlan | null => {
  const match = message.match(/^Merge (\S+) into target (\S+): (.+)$/)
  if (!match) {
    return null
  }
  return {
    mergeContactId: match[1],
    targetContactId: match[2],
    reason: match[3],
  }
}

export const extractMergePlan = (
  plan: Record<string, unknown> | null | undefined
): MergePlan | null => {
  if (!plan) {
    return null
  }

  const targetContactId = typeof plan.targetContactId === 'string' ? plan.targetContactId : null
  const mergeContactId = mergeContactIdFromPlan(plan)
  const reason = typeof plan.reason === 'string' ? plan.reason : null

  if (targetContactId || mergeContactId) {
    return {
      targetContactId: targetContactId ?? '',
      mergeContactId,
      reason,
    }
  }

  if (plan.terminal !== 'MERGE') {
    return null
  }

  return null
}

const hasMergeTarget = (plan: MergePlan | null): plan is MergePlan =>
  Boolean(plan && (plan.targetContactId || plan.mergeContactId))

const pairContactIdsFromSteps = (steps: TraceStep[]): string[] | null => {
  for (const step of [...steps].reverse()) {
    if (step.event !== 'contact_scored' || !step.metadata) {
      continue
    }
    const ids = step.metadata.pairContactIds ?? step.metadata.contactIds
    if (
      Array.isArray(ids) &&
      ids.length === 2 &&
      ids.every((id): id is string => typeof id === 'string')
    ) {
      return ids
    }
  }
  return null
}

const inferMergeFromFinalContact = (trace: {
  finalCompanyId?: string | null
  terminalReason?: string | null
  summary?: string | null
  companies?: { companyId: string }[]
  steps: TraceStep[]
}): MergePlan | null => {
  if (!trace.finalCompanyId) {
    return null
  }

  const pairIds = pairContactIdsFromSteps(trace.steps)
  const contactIds =
    pairIds ??
    (trace.companies?.length === 2 ? trace.companies.map((company) => company.companyId) : null)

  if (!contactIds || contactIds.length !== 2) {
    return null
  }

  const mergeContactId = contactIds.find((id) => id !== trace.finalCompanyId) ?? null
  const reason =
    trace.terminalReason ??
    (trace.summary ? parseMergeMessage(trace.summary)?.reason : null) ??
    null

  return {
    targetContactId: trace.finalCompanyId,
    mergeContactId,
    reason,
  }
}

const hasMergeDecisionStep = (steps: TraceStep[]): boolean =>
  steps.some((step) => {
    if (step.event !== 'group_decided') {
      return false
    }
    if (step.message && parseMergeMessage(step.message)) {
      return true
    }
    const metadata = step.metadata
    if (!metadata) {
      return false
    }
    const plan = metadata.plan as Record<string, unknown> | undefined
    const terminal =
      metadata.terminal ?? (typeof plan?.terminal === 'string' ? plan.terminal : null)
    return terminal === 'MERGE'
  })

const isMergeGroup = (trace: {
  terminal?: string | null
  decidedAction?: string | null
  plan?: Record<string, unknown> | null
  finalCompanyId?: string | null
  companies?: { companyId: string }[]
  steps?: TraceStep[]
}): boolean => {
  const terminal = trace.terminal ?? trace.decidedAction
  if (terminal === 'MERGE' || trace.plan?.terminal === 'MERGE') {
    return true
  }
  if (trace.steps && hasMergeDecisionStep(trace.steps)) {
    return true
  }
  if (planHasMergeTarget(trace.plan ?? null)) {
    return true
  }
  return Boolean(trace.finalCompanyId && trace.companies && trace.companies.length === 2)
}

const planHasMergeTarget = (plan: Record<string, unknown> | null): boolean =>
  typeof plan?.targetContactId === 'string' && plan.targetContactId.length > 0

export const mergePlanFromSteps = (steps: TraceStep[]): MergePlan | null => {
  for (const step of [...steps].reverse()) {
    if (step.event !== 'group_decided' && step.event !== 'contact_merged') {
      continue
    }

    if (step.metadata) {
      const plan = extractMergePlan(
        (step.metadata.plan as Record<string, unknown> | undefined) ?? step.metadata
      )
      if (hasMergeTarget(plan)) {
        return plan
      }
    }

    if (step.message) {
      const parsed = parseMergeMessage(step.message)
      if (hasMergeTarget(parsed)) {
        return parsed
      }
    }
  }
  return null
}

export const mergePlanFromTrace = (trace: {
  terminal?: string | null
  decidedAction?: string | null
  finalCompanyId?: string | null
  terminalReason?: string | null
  summary?: string | null
  plan?: Record<string, unknown> | null
  companies?: { companyId: string }[]
  steps: TraceStep[]
}): MergePlan | null => {
  const fromSteps = mergePlanFromSteps(trace.steps)
  if (hasMergeTarget(fromSteps)) {
    return fromSteps
  }

  const fromPlan = extractMergePlan(trace.plan)
  if (hasMergeTarget(fromPlan)) {
    return fromPlan
  }

  if (trace.summary) {
    const fromSummary = parseMergeMessage(trace.summary)
    if (hasMergeTarget(fromSummary)) {
      return fromSummary
    }
  }

  if (isMergeGroup(trace)) {
    return inferMergeFromFinalContact(trace)
  }

  return null
}

export const contactMergeOutcomeLabel = (
  contactId: string,
  mergePlan: MergePlan | null
): string | null => {
  if (!mergePlan?.targetContactId) {
    return null
  }
  if (contactId === mergePlan.targetContactId) {
    return 'Target (kept)'
  }
  if (contactId === mergePlan.mergeContactId) {
    return `Merged into ${mergePlan.targetContactId}`
  }
  return null
}
